import type {
  ResourceCRDDefinition,
  ResourceCondition,
  ResourceDetailColumn,
  ResourceDetailResponse,
  ResourceJobDetail,
  ResourceRelatedEvents,
  ResourceRowCRD,
} from "@k8s-dashboard/contracts";
import { useT } from "@/i18n/I18nProvider";
import { ResourceBuiltinDetailSections } from "./ResourceBuiltinDetailSections";
import type { BuiltinRelationsView } from "./builtin-relations";

/**
 * 상세의 구조화 절 (ADR 0065 C1 결정 3 · C2 결정 5·6 · C3)
 * --------------------------------------------------------------------------
 * 전부 **서버가 정제·평가한 값**입니다. 브라우저는 전체 객체도 JSONPath도
 * 받지 않고, 여기서 계산하는 것도 없습니다 — 받은 것을 그대로 보여 줍니다.
 *
 * 비어 있는 절은 그리지 않습니다. 다만 관련 Event는 **예외**입니다 —
 * "꺼짐 · 인덱스 없음 · 권한 없음 · 경고 없음"이 서로 다른 사실이고, 넷을 같은
 * 빈 화면으로 처리하면 운영자는 경고가 없다고 읽습니다(ADR 0002).
 */

/** 절 하나의 껍데기. 제목 마크업을 네 곳에 복사하지 않습니다. */
export function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="resource-detail__section">
      <h3 className="resource-detail__heading">{title}</h3>
      {children}
    </section>
  );
}

/**
 * 표 하나. **마크업이 한 벌입니다.**
 *
 * 정의 목록·conditions·관련 Event가 전부 "머리글 + 행"이고, 복사해 두면 한 곳만
 * 고쳐져 표마다 caption·접근성 모양이 달라집니다. 셀은 ReactNode라 버튼·뱃지도
 * 그대로 들어갑니다.
 */
export function DataTable({
  caption,
  headers,
  rows,
  interactive,
}: {
  caption: string;
  headers: [string, number?][];
  rows: { key: string; selected?: boolean; cells: React.ReactNode[] }[];
  interactive?: boolean;
}) {
  return (
    <table
      className={`ds-data-table ds-data-table--compact${interactive ? " ds-data-table--interactive" : ""}`}
    >
      <caption className="visually-hidden">{caption}</caption>
      <thead>
        <tr>
          {headers.map(([label, width]) => (
            /* `scope="col"`을 붙이는 실측 근거: Playwright의 역할 해석은 `scope`가
               없는 `th`를 `cell`로 돌려줍니다(`injectedScriptSource`의 TH 분기 —
               측정에서 columnheader가 0개로 나왔습니다). jsdom/Testing Library는
               위치로 추론해 통과했으므로 단위 테스트만으로는 드러나지 않았습니다.
               이 저장소의 다른 표도 같은 속성을 씁니다. 그 밖의 도구·스크린리더
               동작은 여기서 주장하지 않습니다. */
            <th key={label} scope="col" style={width ? { width } : undefined}>
              {label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key} className={row.selected ? "is-selected" : ""}>
            {row.cells.map((cell, i) => (
              <td
                key={headers[i]?.[0] ?? i}
                className={i === 0 ? "ds-ident" : undefined}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** 이름·값 쌍 목록. CRD 이름 필드와 상세 열이 같은 마크업을 공유합니다. */
export function Facts({ rows }: { rows: [string, React.ReactNode][] }) {
  return (
    <dl className="resource-drawer__facts">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd className="ds-ident">{value || "-"}</dd>
        </div>
      ))}
    </dl>
  );
}

/** CRD 정의의 구조화 필드. schema는 들어 있지 않습니다(YAML에서만 봅니다). */
export function CRDDefinitionSection({
  crd,
  explorer,
}: {
  crd: ResourceCRDDefinition;
  explorer?: ResourceRowCRD;
}) {
  const { t } = useT("resources");
  return (
    <Section title={t("resources.def")}>
      <Facts
        rows={[
          [t("resources.def.scope"), crd.scope ?? ""],
          [t("resources.def.kind"), crd.names.kind ?? ""],
          [t("resources.col.plural"), crd.names.plural ?? ""],
          [t("resources.def.singular"), crd.names.singular ?? ""],
          [t("resources.def.short"), crd.names.shortNames?.join(", ") ?? ""],
          [t("resources.def.stored"), crd.storedVersions?.join(", ") ?? ""],
        ]}
      />

      {explorer && (
        <p
          className="resource-crd__membership"
          role="note"
          data-state={explorerState(explorer)}
        >
          {explorer.known
            ? explorer.allowlisted
              ? t("resources.in.yes")
              : t("resources.in.no.d", { gvr: explorer.selectedGvr ?? "" })
            : t("resources.in.unknown")}
          {!explorer.allowlisted && explorer.suggestedEntry && (
            <>
              {" "}
              <code className="ds-ident">{explorer.suggestedEntry}</code>
            </>
          )}
        </p>
      )}

      {crd.versions && crd.versions.length > 0 && (
        /* 버전별 사실을 **이름·값 쌍**으로 둡니다 — 제공·저장·열이 한 줄에 다 들어가고,
           표 마크업 한 벌이 줄어듭니다. priority > 0 열은 `*`로 구분합니다. */
        <Facts
          rows={crd.versions.map((v) => [
            v.name === crd.selectedVersion
              ? `${v.name} · ${t("resources.def.sel")}`
              : v.name,
            [
              v.served ? t("resources.def.served") : "",
              v.storage ? t("resources.def.storage") : "",
              v.columns?.length
                ? v.columns
                    .map((c) => `${c.name}${c.priority > 0 ? "*" : ""}`)
                    .join(", ")
                : t("resources.def.nocols"),
            ]
              .filter(Boolean)
              .join(" · "),
          ])}
        />
      )}
      {/* schema를 구조화하지 않는 것은 **결정**입니다. 왜 아래 YAML을 봐야 하는지 말해 줍니다. */}
      <p className="muted" role="note">
        {t("resources.def.schema")}
      </p>
      {crd.truncated && (
        <p className="muted" role="note">
          {t("resources.cut")}
        </p>
      )}
    </Section>
  );
}

function explorerState(explorer: ResourceRowCRD): string {
  if (!explorer.known) return "unknown";
  return explorer.allowlisted ? "included" : "excluded";
}

/** priority > 0 printer column. 목록에는 없고 상세에만 있습니다. */
export function DetailColumnsSection({
  columns,
}: {
  columns: ResourceDetailColumn[];
}) {
  const { t } = useT("resources");
  return (
    <Section title={t("resources.cols")}>
      <Facts rows={columns.map((c) => [c.name, c.value ?? ""])} />
    </Section>
  );
}

/** status.conditions. 서버가 길이를 조이고 시각을 다시 쓴 값입니다. */
export function ConditionsSection({
  conditions,
}: {
  conditions: ResourceCondition[];
}) {
  const { t } = useT("resources");
  return (
    <Section title={t("resources.cond")}>
      <DataTable
        caption={t("resources.cond")}
        headers={[
          [t("resources.cond.type")],
          [t("resources.cond.status"), 110],
          [t("resources.cond.reason")],
          [t("resources.at"), 200],
        ]}
        rows={conditions.map((c) => ({
          key: `${c.type}/${c.status ?? ""}`,
          /* 상태는 색만으로 구분하지 않습니다 — 값 자체가 텍스트입니다(ADR 0001). */
          cells: [
            c.type,
            c.status || "-",
            c.reason || "-",
            c.lastTransitionTime || "-",
          ],
        }))}
      />
    </Section>
  );
}

/** 기존 격리 GET 하나에서 온 Job 닫힌 투영. YAML·Pod template를 다시 해석하지 않습니다. */
export function JobDetailSection({ job }: { job: ResourceJobDetail }) {
  const { t } = useT("resources");
  const text = (value: {
    state: string;
    value?: string;
    truncated?: boolean;
  }) =>
    value.state === "withheld_too_large"
      ? t("resources.job.withheld")
      : value.state === "absent"
        ? t("resources.job.absent")
        : `${value.value ?? ""}${value.truncated ? ` ${t("resources.job.truncated")}` : ""}`;
  return (
    <Section title={t("resources.job")}>
      <Facts
        rows={[
          [
            t("resources.job.parallelism"),
            job.parallelism?.toString() ?? t("resources.job.absent"),
          ],
          [
            t("resources.job.completions"),
            job.completions?.toString() ?? t("resources.job.absent"),
          ],
        ]}
      />
      {job.conditions.items.length > 0 && (
        <DataTable
          caption={t("resources.job.conditions")}
          headers={[
            [t("resources.cond.type")],
            [t("resources.cond.status"), 110],
            [t("resources.cond.reason")],
            [t("resources.ev.msg")],
          ]}
          rows={job.conditions.items.map((condition) => ({
            key: condition.type,
            cells: [
              condition.type,
              condition.status,
              text(condition.reason),
              text(condition.message),
            ],
          }))}
        />
      )}
      {job.conditions.omitted ? (
        <p className="muted">
          {t("resources.job.omitted", { count: job.conditions.omitted })}
        </p>
      ) : null}
    </Section>
  );
}

/**
 * 관련 Warning Event (ADR 0065 C3).
 *
 * **언제나 그립니다.** 네 상태가 서로 다른 사실이기 때문입니다 —
 * 꺼짐 / 인덱스 없음 / 권한 없음 / 경고 없음. 넷을 같은 빈 목록으로 보여 주면
 * 운영자는 "경고가 없다"로 읽습니다.
 */
/**
 * 상태 → (톤, 제목 ID). **ID는 리터럴입니다** — 템플릿으로 만들면 i18n 게이트가
 * "쓰이지 않는 문구"로 보고, 지워도 아무도 모르는 문구가 생깁니다.
 *
 * 설명 문장은 두지 않습니다. 네 제목이 이미 서로 다른 사실을 말하고
 * (`꺼져 있음` · `인덱스 없음` · `권한 없음` · `경고 없음`), 기계가 읽는 구분은
 * `data-state`가 담습니다 — 같은 말을 네 번 번역해 번들에 싣지 않습니다.
 */
const EVENT_STATE = {
  disabled: ["warning", "resources.ev.disabled"],
  unavailable: ["warning", "resources.ev.unavailable"],
  forbidden: ["forbidden", "resources.ev.forbidden"],
  ready: ["neutral", "resources.ev.ready"],
} as const;

export function RelatedEventsSection({
  events,
}: {
  events?: ResourceRelatedEvents;
}) {
  const { t } = useT("resources");
  /* 계약은 이 절을 **언제나** 싣습니다. 그래도 없을 때를 "경고 없음"으로 읽지
     않습니다 — 절이 빠진 응답은 "읽지 못했다"는 사실이고, 그것이 unavailable입니다.
     이 방어가 없으면 필드 하나가 빠진 응답이 드로어 전체를 날립니다. */
  const state: keyof typeof EVENT_STATE =
    !events || events.state === "unavailable" ? "unavailable" : events.state;
  const items = state === "ready" ? (events?.items ?? []) : [];

  /* 네 상태가 서로 다른 문장입니다. 분기를 늘리는 대신 **표**로 둡니다 —
     한 곳에서 읽히고, 번들에도 네 벌의 마크업이 생기지 않습니다. */
  if (items.length === 0) {
    const [tone, titleId] = EVENT_STATE[state];
    return (
      <Section title={t("resources.ev")}>
        <div
          className={`state state--${tone}`}
          role={tone === "forbidden" ? "alert" : "note"}
          data-state={state}
        >
          <span className="state__glyph" aria-hidden="true">
            {tone === "forbidden" ? "!" : tone === "neutral" ? "✓" : "▲"}
          </span>
          <span className="state__title">{t(titleId)}</span>
        </div>
      </Section>
    );
  }
  return (
    <Section title={t("resources.ev")}>
      <DataTable
        caption={t("resources.ev")}
        headers={[
          [t("resources.cond.reason"), 170],
          [t("resources.ev.msg")],
          [t("resources.ev.n"), 80],
          [t("resources.at"), 200],
        ]}
        rows={items.map((ev) => ({
          key: ev.id,
          cells: [
            ev.reason || "-",
            ev.message || "-",
            ev.count,
            ev.lastSeen || "-",
          ],
        }))}
      />
      <p className="muted" role="note">
        {t("resources.ev.warn")}
        {events?.truncated ? ` ${t("resources.cut")}` : ""}
      </p>
    </Section>
  );
}

/** 상세 하나의 구조화 절 전부. 순서는 "무엇인가 → 지금 어떤가 → 무엇이 터졌나"입니다. */
export function ResourceDetailSections({
  detail,
  relations,
  onResourceLink,
}: {
  detail: ResourceDetailResponse;
  relations?: BuiltinRelationsView;
  onResourceLink?: (
    link: import("@k8s-dashboard/contracts").ResourceBuiltinLink,
  ) => void;
}) {
  return (
    <>
      {detail.crd && (
        <CRDDefinitionSection crd={detail.crd} explorer={detail.crdExplorer} />
      )}
      {detail.columns && detail.columns.length > 0 && (
        <DetailColumnsSection columns={detail.columns} />
      )}
      {detail.conditions && detail.conditions.length > 0 && (
        <ConditionsSection conditions={detail.conditions} />
      )}
      {detail.job && <JobDetailSection job={detail.job} />}
      {detail.builtin && (
        <ResourceBuiltinDetailSections
          builtin={detail.builtin}
          relations={relations}
          onResourceLink={onResourceLink}
        />
      )}
      {/* 관련 Event 절은 **언제나** 그립니다 — 상태가 사실이기 때문입니다. */}
      <RelatedEventsSection events={detail.relatedEvents} />
    </>
  );
}
